/**
 * 管理后台「账号」列表里**状态徽章**的显示口径（纯函数，可单测）。
 *
 * ## 为什么单拎出来
 *
 * 2026-10 用户看管理后台「账号」页（角色筛选＝学生）指出：
 *
 * > 学生没有邮箱，这里"邮箱未验证"不需要吧？
 *
 * 原来的写法是一刀切的二选一：
 *
 * ```tsx
 * {user.emailVerifiedAt ? <Badge>邮箱已验证</Badge> : <Badge>邮箱未验证</Badge>}
 * ```
 *
 * 学生账号是家长在「学生」里直接建的，**根本没有邮箱**（`students.service.ts`
 * 建号时 `email: null`），于是 `emailVerifiedAt` 必然是 `null` —— 列表里每个学生
 * 都挂着「邮箱未验证」，而它上面那行已经写着「（无邮箱）」。用户看到的是
 * 一个**永远不会变、也永远没救**的告警标记：没有邮箱可验证，点不进任何流程。
 * 这正是项目原则里说的"不要显示无意义的标记"。
 *
 * 「邮箱已验证」那一半也有同样的问题：给一个没有邮箱的账号盖"已验证"的章，
 * 反而像在说"这个账号有邮箱且验证过了"。
 *
 * ## 现在的口径
 *
 * | 账号有没有邮箱 | 有没有验证过 | 状态列的邮箱徽章 |
 * | --- | --- | --- |
 * | 有 | 是 | 「邮箱已验证」 |
 * | 有 | 否 | 「邮箱未验证」（**保留**，这是真的需要处理的账号） |
 * | 无 | （必然没验证过）| **什么都不画** —— 账号列已经写了「（无邮箱）」 |
 *
 * 刻意**不**把"无邮箱"做成一个徽章：同一行里会显示两遍同一件事，那是噪音不是信息。
 *
 * ## 2026-10 第二次修正：状态列里「正常」与「已锁定」会同时出现
 *
 * 「状态」那一列原来把三种**不同维度**的东西混在一起画：
 *
 * | 维度 | 徽章 | 会不会同时出现 |
 * | --- | --- | --- |
 * | 生命周期 | 正常 / 已停用 / 已锁定 | **最多只能有一个** |
 * | 凭据 | 邮箱已验证 / 邮箱未验证 / 已绑动态口令 | 各自独立，可以并存 |
 * | 安全 | 失败 N 次 | 与凭据类可并存（是另一件事） |
 *
 * 生命周期那三个原来不是一个整体：`deletedAt ? 已停用 : 正常` 与独立的
 * `locked ? 已锁定 : null` 各画各的 —— 于是"未停用但被锁定"的账号会同时挂上
 * 绿色的「正常」和红色的「已锁定」，同一行自相矛盾（用户 2026-10 报的就是它）。
 *
 * 现在这三者由 `accountLifecycleState()` **一处**判定、三选一，整列由
 * `accountStatusBadges()` 一次算完 —— 组件只负责把数组画出来，不再自己拼条件。
 * 判据优先级：**已停用 > 已锁定 > 正常**（停用是最根本的状态，停用后不再提锁定）。
 *
 * ⚠️ 「状态」筛选下拉只有 `全部 / 正常 / 已停用` 三项（后端 `status` 也只认
 * `active` / `disabled`，见 `admin.service.ts`）：它筛的是**停用**这一件事，
 * 所以下拉里的「正常」= "未停用"，**包含**被锁定的账号 —— 那些行按上面的规则
 * 显示「已锁定」。这是刻意的：锁定是凭据安全状态，不是第四个筛选档。
 */
import type { AdminUserRow } from './api/admin';

/** 邮箱徽章的三种状态。`none` = 这一格什么都不渲染（不是渲染成空徽章）。 */
export type EmailBadgeState = 'verified' | 'unverified' | 'none';

/** 这个账号到底有没有邮箱。空白字符串不算有（后端给的是 `null`，这里也兜一层）。 */
export function accountHasEmail(email: string | null | undefined): boolean {
  return typeof email === 'string' && email.trim().length > 0;
}

/**
 * 账号的邮箱徽章该显示哪一种。
 *
 * ⚠️ `emailVerifiedAt` 只在**有邮箱**时才有意义。没有邮箱的账号（学生）必然是
 * `null`，但那不代表"未验证"，所以这里必须先看邮箱。
 */
export function emailBadgeState(
  email: string | null | undefined,
  emailVerifiedAt: string | null | undefined,
): EmailBadgeState {
  if (!accountHasEmail(email)) return 'none';
  return emailVerifiedAt ? 'verified' : 'unverified';
}

/** 徽章文案；`none` 时返回 `null`，调用点据此**不渲染**（而不是渲染空串）。 */
export function emailBadgeLabel(state: EmailBadgeState): string | null {
  switch (state) {
    case 'verified':
      return '邮箱已验证';
    case 'unverified':
      return '邮箱未验证';
    case 'none':
      return null;
  }
}

// ---------------------------------------------------------------- 生命周期

/** 徽章色调。与 `components/ui/Card.tsx` 的 `Badge` 同值（那边没导出这个联合类型）。 */
export type AccountBadgeTone = 'neutral' | 'brand' | 'success' | 'warn' | 'danger';

/**
 * 账号的**生命周期**状态 —— 三者互斥，同一行最多只画一个徽章。
 *
 *  - `disabled`：已停用（软删，`deleted_at` 非空，可由管理员启用恢复）
 *  - `locked`：已锁定（`lockedUntil` 落在**未来**，通常是连续登录失败触发）
 *  - `active`：正常
 */
export type AccountLifecycleState = 'active' | 'disabled' | 'locked';

/**
 * 判定生命周期只需要这两个字段 —— 声明成结构类型而不是直接吃 `AdminUserRow`，
 * 这样 `AdminUserRow` 结构上就能传进来，而测试不必造一整行账号。
 */
export interface AccountLifecycleInput {
  deletedAt: string | null;
  lockedUntil: string | null;
}

/**
 * 生命周期：**已停用 > 已锁定 > 正常**，互斥。
 *
 * `nowMs` 必须由调用方传进来（而不是在函数里读 `Date.now()`）：
 * "锁没锁"是按**当下**算的，把"当下"变成参数，这个判定才是纯函数、才测得准。
 *
 * ⚠️ 已停用的账号即使 `lockedUntil` 还没到，也只报「已停用」：停用之后
 * "密码错了几次"已经不重要了，再挂一枚「已锁定」只会让同一行出现两个红徽章。
 */
export function accountLifecycleState(
  user: AccountLifecycleInput,
  nowMs: number,
): AccountLifecycleState {
  if (user.deletedAt) return 'disabled';
  if (user.lockedUntil && new Date(user.lockedUntil).getTime() > nowMs) return 'locked';
  return 'active';
}

/** 生命周期徽章的文案与色调。文案与「状态」筛选下拉**逐字一致**（正常 / 已停用）。 */
export const ACCOUNT_LIFECYCLE_BADGE: Record<
  AccountLifecycleState,
  { label: string; tone: AccountBadgeTone }
> = {
  active: { label: '正常', tone: 'success' },
  disabled: { label: '已停用', tone: 'danger' },
  locked: { label: '已锁定', tone: 'danger' },
};

/** 「状态」列里一枚徽章的稳定标识（给 React `key` 与测试断言用）。 */
export type AccountStatusBadgeKey = 'lifecycle' | 'admin' | 'email' | 'totp' | 'failedAttempts';

export interface AccountStatusBadge {
  key: AccountStatusBadgeKey;
  label: string;
  tone: AccountBadgeTone;
}

/** 「状态」列要看的全部字段（`AdminUserRow` 结构上可直接传入）。 */
export interface AccountStatusBadgeInput extends AccountLifecycleInput {
  role: AdminUserRow['role'];
  email: string | null;
  emailVerifiedAt: string | null;
  totpEnabledAt: string | null;
  failedAttempts: number;
}

/**
 * 「状态」列**整列**的徽章序列 —— 纯函数，一棵树里只此一处判定。
 *
 * 顺序（也是画出来的从左到右）：生命周期 → 管理员 → 邮箱 → 动态口令 → 失败次数。
 *
 * 两条刻意保留的口径（都是**不同维度**，允许并存）：
 *  1. 邮箱徽章仍走 `emailBadgeState()`：没有邮箱的账号（学生）**什么都不画**；
 *  2. 「失败 N 次」是**安全**维度，与生命周期并存 —— 但账号**已锁定时不再画**它，
 *     因为锁定本来就是"失败次数太多"的结果，两枚徽章说的是同一件事。
 *     （这是改动前的口径，逐字保留：`!locked && failedAttempts > 0`。）
 */
export function accountStatusBadges(
  user: AccountStatusBadgeInput,
  nowMs: number,
): AccountStatusBadge[] {
  const badges: AccountStatusBadge[] = [];

  const lifecycle = accountLifecycleState(user, nowMs);
  const lifecycleBadge = ACCOUNT_LIFECYCLE_BADGE[lifecycle];
  badges.push({ key: 'lifecycle', label: lifecycleBadge.label, tone: lifecycleBadge.tone });

  if (user.role === 'system_admin') {
    badges.push({ key: 'admin', label: '管理员', tone: 'brand' });
  }

  const emailState = emailBadgeState(user.email, user.emailVerifiedAt);
  const emailLabel = emailBadgeLabel(emailState);
  if (emailLabel !== null) {
    badges.push({
      key: 'email',
      label: emailLabel,
      tone: emailState === 'verified' ? 'neutral' : 'warn',
    });
  }

  if (user.totpEnabledAt) {
    badges.push({ key: 'totp', label: '已绑动态口令', tone: 'neutral' });
  }

  if (lifecycle !== 'locked' && user.failedAttempts > 0) {
    badges.push({ key: 'failedAttempts', label: `失败 ${user.failedAttempts} 次`, tone: 'warn' });
  }

  return badges;
}
